/** Compare columns Ops can assign. Omitted keys keep the survivor's value. */
export const MERGE_FIELD_KEYS = [
  'name',
  'sku',
  'barcode',
  'brand',
  'category',
  'cost_price',
  'selling_price',
  'mrp',
  'hsn',
  'gst_rate',
  'status',
  'image_keys',
];

const FORBIDDEN_FIELD_KEYS = ['item_id', 'sold_by', 'stock_qty', 'created_at'];

export const SOLD_BY_MISMATCH_COPY = 'Pack vs Loose mismatch. Merge stays disabled until every product is sold the same way.';

export function soldByOf(item) {
  return String(item?.soldBy || item?.sold_by || 'EACH').toUpperCase() === 'WEIGHT' ? 'WEIGHT' : 'EACH';
}

export function soldAsLabel(item) {
  return soldByOf(item) === 'WEIGHT' ? 'Loose' : 'Pack';
}

/** Barcodes that still open the survivor, excluding its primary barcode. */
export function collectBarcodeAliases(primary, codes) {
  const main = String(primary || '').trim();
  const seen = new Set();
  const aliases = [];
  (codes || []).forEach((code) => {
    const value = String(code || '').trim();
    if (!value || value === main || seen.has(value)) return;
    seen.add(value);
    aliases.push(value);
  });
  return aliases;
}

export function productMatchesBarcode(product, code) {
  const value = String(code || '').trim();
  if (!value) return false;
  if (String(product?.barcode || '').trim() === value) return true;
  return (product?.barcodeAliases || []).some((alias) => String(alias).trim() === value);
}

export function soldByMismatch(items) {
  const list = items || [];
  if (list.length < 2) return false;
  const first = soldByOf(list[0]);
  return list.some((item) => soldByOf(item) !== first);
}

/** Preview only. The server recomputes on-hand from open lots. */
export function previewOnHand(items) {
  return (items || []).reduce((sum, item) => {
    const n = Number(item?.stock ?? item?.stock_qty ?? 0);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
}

function itemId(item) {
  return item?.id || item?.item_id || '';
}

function statusOf(item) {
  return String(item?.status || '').toUpperCase();
}

/**
 * Attribute → source item id. Survivor winners are omitted.
 * barcode_type follows the barcode winner. status is sent only for ACTIVE or INACTIVE.
 */
export function buildMergeFields(items, survivorId, winners = {}) {
  const list = items || [];
  const allowed = new Set(list.map(itemId).filter(Boolean));
  const fields = {};

  MERGE_FIELD_KEYS.forEach((key) => {
    const winner = winners[key] || survivorId;
    if (!winner || winner === survivorId || !allowed.has(winner)) return;
    if (key === 'status') {
      const source = list.find((item) => itemId(item) === winner);
      const status = statusOf(source);
      if (status !== 'ACTIVE' && status !== 'INACTIVE') return;
    }
    fields[key] = winner;
  });

  if (fields.barcode) fields.barcode_type = fields.barcode;

  FORBIDDEN_FIELD_KEYS.forEach((key) => {
    delete fields[key];
  });
  return fields;
}

export function buildMergeBody({ survivorId, donorIds, items, winners } = {}) {
  return {
    survivor_id: survivorId,
    donor_ids: donorIds,
    fields: buildMergeFields(items, survivorId, winners),
  };
}

export function isMergedProduct(item) {
  return statusOf(item) === 'MERGED';
}

/** Split an updated_since page into rows to keep and ids to drop. */
export function splitMergedDelta(items) {
  const live = [];
  const mergedIds = [];
  (items || []).forEach((item) => {
    const id = itemId(item);
    if (!id) return;
    if (isMergedProduct(item)) mergedIds.push(id);
    else live.push(item);
  });
  return { live, mergedIds };
}

export function tombstoneTarget(err) {
  if (err?.code !== 'merged' && err?.body?.error !== 'merged') return null;
  return err?.body?.merged_into || err?.merged_into || null;
}

/** 200 with idempotent: true is the same merge, not a failure. */
export function isMergeSuccess(response) {
  return Boolean(response && response.merge_id && Array.isArray(response.donor_ids));
}

export function mergeErrorCopy(err) {
  const code = err?.code || err?.body?.error || '';
  const body = err?.body || {};
  if (code === 'invalid_merge') {
    const reason = typeof body.reason === 'string' ? body.reason : '';
    return {
      code,
      message: reason || 'Fix the selection or which product wins each field.',
    };
  }
  if (code === 'forbidden') {
    return { code, hide: true, message: 'You do not have permission to merge products.' };
  }
  if (code === 'not_found') {
    return { code, refresh: true, message: 'One of these products is missing. Refresh the catalog.' };
  }
  if (code === 'sold_by_mismatch') {
    return { code, message: SOLD_BY_MISMATCH_COPY };
  }
  if (code === 'merge_blocked') {
    const subscriptionIds = Array.isArray(body.subscription_ids) ? body.subscription_ids : [];
    const itemIds = Array.isArray(body.item_ids) ? body.item_ids : [];
    const subText = subscriptionIds.length ? ` Subscriptions: ${subscriptionIds.join(', ')}.` : '';
    const itemText = itemIds.length ? ` Products: ${itemIds.join(', ')}.` : '';
    return {
      code,
      subscriptionIds,
      itemIds,
      message: `Finish or cancel those subscriptions, then merge.${subText}${itemText}`,
    };
  }
  if (code === 'merged_elsewhere') {
    return {
      code,
      refresh: true,
      message: 'That duplicate was already merged into a different product. Refresh the catalog.',
    };
  }
  if (code === 'merged') {
    return {
      code,
      mergedInto: body.merged_into || null,
      message: 'This product was already merged.',
    };
  }
  if (code === 'merge_in_progress') {
    return { code, retry: true, message: 'Another merge is in progress.' };
  }
  return { code: code || 'error', message: err?.detail || err?.message || 'Could not merge these products.' };
}
